/**
 * CalendarTimeGrid (DESIGN.md T13): day / work week / week view. Day headers, an all-day row, a 24-hour scrolling
 * grid (scrolled to the working day on open), overlapping events side by side and the now-line.
 *
 * Direct manipulation like Outlook (pointer: mouse, pen, touch with long-press; snapping to 15 minutes, live preview,
 * Esc cancels): drag on free space to create (a click gives 30 minutes), drag across the all-day row for an all-day
 * event, drag an event to move it (time and day), drag its top/bottom edge to resize. Keyboard: on a focused event
 * Alt+arrows move it (15 minutes / one day), Shift+Alt+Up/Down change its end; the change is saved shortly after the
 * last key. Only events `canDrag` allows are movable. Events are buttons (title + time via `eventLabel`).
 */
import { useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent } from 'react';
import { cn } from '../lib/utils';
import { allDayRange, createRange, minutesAt, moveTo, nudge, resizeEdge, sameRange, SNAP_MINUTES } from './drag';
import { EventChip } from './event-chip';
import { addDays, DAY_MS, isMultiDay, layoutDay, layoutWeek, overlapsDay, sameDay, startOfDay, type CalendarGridEvent } from './model';
import { inTimeOrder, rovingKeyDown, tabStopOf } from './roving';
import { usePointerDrag } from './use-pointer-drag';

export interface CalendarTimeGridLabels {
  allDay: string;
  /** Accessible name of the grid region. */
  grid: string;
}

export interface CalendarTimeGridProps {
  days: readonly Date[];
  events: readonly CalendarGridEvent[];
  now: Date;
  selectedId?: string | undefined;
  onSelectEvent: (id: string) => void;
  /** Click or drag on free space selects a range (30 minutes for a click; whole days in the all-day row). */
  onSelectRange?: ((start: Date, end: Date, allDay: boolean) => void) | undefined;
  /** The selected free range (stays highlighted until changed). */
  selection?: { start: Date; end: Date; allDay: boolean } | undefined;
  /** Double-click on free space: act on the selection (e.g. quick create). */
  onOpenSelection?: (() => void) | undefined;
  /** May the user move/resize this event? (default: no) */
  canDrag?: ((event: CalendarGridEvent) => boolean) | undefined;
  /** An event was moved or resized (not called when it ended where it started). */
  onChangeEvent?: ((id: string, start: Date, end: Date) => void) | undefined;
  /** Ctrl/Alt+drag: a copy of the event at the new place. */
  onCopyEvent?: ((id: string, start: Date, end: Date) => void) | undefined;
  /** Click on a day header (e.g. switch to that day). */
  onSelectDay?: ((day: Date) => void) | undefined;
  formatWeekday: (day: Date) => string;
  formatDayNumber: (day: Date) => string;
  formatHour: (hour: number) => string;
  /** Accessible name / tooltip of an event (title, time, location). */
  eventLabel: (event: CalendarGridEvent) => string;
  formatTimeRange: (event: CalendarGridEvent) => string;
  labels: CalendarTimeGridLabels;
  /** Hour shown at the top on open (default: start of the working hours, else 7). */
  scrollToHour?: number;
  /** Height of one hour (rem; default 3 ≈ 13 hours on a 1440×900 screen). */
  hourHeight?: number;
  /** Scale the grid so the working hours fill the visible height (needs `workingHours`). */
  fitWorkingHours?: boolean;
  /** Working time: hours outside it (and non-working days, 0 = Sunday) are shaded. */
  workingHours?: { startMinutes: number; endMinutes: number; days: readonly number[] } | undefined;
  /** Ctrl+wheel or pinch on the grid: zoom in (1) or out (-1). */
  onZoom?: ((direction: 1 | -1) => void) | undefined;
  /** Label of the selected or dragged free range ("09:00–11:30 · 2 h 30 min"). */
  formatSelection?: ((range: { start: Date; end: Date; allDay: boolean }) => string) | undefined;
  /** Second hour column (another time zone): label and hour label for a local hour. */
  secondaryZone?: { label: string; formatHour: (hour: number) => string } | undefined;
  /** Label of the primary hour column when a secondary one is shown. */
  primaryZoneLabel?: string | undefined;
  /** Week number shown in the corner (week/work week views). */
  weekLabel?: string | undefined;
  className?: string;
}

const HOURS = Array.from({ length: 24 }, (_, hour) => hour);
/** Default hour height (rem). */
const HOUR_REM = 3;
/** Root font size used to turn rem into pixels for layout decisions. */
const REM_PX = 16;
/** Smallest hour height "fit working hours" may choose (px). */
const MIN_FIT_HOUR_PX = 24;
/** Wheel delta (px) of one zoom step (a trackpad pinch sends many small deltas). */
const ZOOM_WHEEL_STEP = 40;
/** Pinch distance ratio of one zoom step. */
const ZOOM_PINCH_STEP = 1.15;
/** Keyboard changes are saved this long after the last key. */
const KEYBOARD_COMMIT_MS = 800;
/** `data-context-id` of free space (day columns, all-day cells) for the app's context menu. */
export const SLOT_CONTEXT_ID = 'calendar-slot';

type Preview = { id?: string | undefined; start: Date; end: Date };

export function CalendarTimeGrid({
  days,
  events,
  now,
  selectedId,
  onSelectEvent,
  onSelectRange,
  selection,
  onOpenSelection,
  canDrag,
  onChangeEvent,
  onCopyEvent,
  onSelectDay,
  formatWeekday,
  formatDayNumber,
  formatHour,
  eventLabel,
  formatTimeRange,
  labels,
  scrollToHour,
  hourHeight = HOUR_REM,
  fitWorkingHours = false,
  workingHours,
  onZoom,
  formatSelection,
  secondaryZone,
  primaryZoneLabel,
  weekLabel,
  className,
}: CalendarTimeGridProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const columnRefs = useRef<(HTMLDivElement | null)[]>([]);
  const allDayRefs = useRef<(HTMLDivElement | null)[]>([]);
  const { begin, suppressClick } = usePointerDrag();
  const [preview, setPreview] = useState<Preview>();
  const [allDayPreview, setAllDayPreview] = useState<{ from: number; to: number }>();
  const keyboard = useRef<{ id: string; timer: ReturnType<typeof setTimeout> } | undefined>(undefined);

  // While a keyboard change waits to be saved, the event is drawn at its new place.
  const shown = useMemo(() => (preview?.id ? events.map((e) => (e.id === preview.id ? { ...e, start: preview.start, end: preview.end } : e)) : events), [events, preview]);
  const gutters = secondaryZone ? 2 : 1;
  const columns = { gridTemplateColumns: `repeat(${gutters}, 3.5rem) repeat(${days.length}, minmax(0, 1fr))` };
  const allDayLayout = useMemo(() => layoutWeek(shown.filter((e) => e.allDay || isMultiDay(e)), days, Number.POSITIVE_INFINITY), [days, shown]);
  const allDayLanes = Math.max(0, ...allDayLayout.shown.map((s) => s.lane + 1));
  const timed = useMemo(() => days.map((day) => layoutDay(shown, day)), [days, shown]);
  const firstDay = days[0]?.getTime();
  const draggable = (event: CalendarGridEvent) => Boolean(onChangeEvent && canDrag?.(event));

  // "Fit working hours": the hour height follows the visible height of the grid.
  const [viewHeight, setViewHeight] = useState(0);
  const fitSpan = workingHours ? Math.max(1, (workingHours.endMinutes - workingHours.startMinutes) / 60) : 0;
  const fitting = fitWorkingHours && fitSpan > 0;
  useEffect(() => {
    const element = scroller.current;
    if (!fitting || !element) return;
    const measure = () => setViewHeight(element.clientHeight);
    measure();
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(measure);
    observer?.observe(element);
    return () => observer?.disconnect();
  }, [fitting]);
  const hourPx = fitting && viewHeight > 0 ? Math.max(MIN_FIT_HOUR_PX, viewHeight / fitSpan) : hourHeight * REM_PX;
  const gridHeight = fitting && viewHeight > 0 ? `${24 * hourPx}px` : `${24 * hourHeight}rem`;
  const topHour = scrollToHour ?? (workingHours ? workingHours.startMinutes / 60 : 7);

  useEffect(() => {
    const element = scroller.current;
    // A little above the hour, so its label is not cut off (fitted: exactly the working day).
    if (element) element.scrollTop = Math.max(0, (element.scrollHeight / 24) * topHour - (fitting ? 0 : 12));
  }, [firstDay, days.length, topHour, fitting, gridHeight]);

  // Ctrl+wheel (and a trackpad pinch, which browsers send as Ctrl+wheel) zooms the hours instead of the page.
  const zoom = useRef(onZoom);
  useEffect(() => {
    zoom.current = onZoom;
  });
  useEffect(() => {
    const element = scroller.current;
    if (!element) return;
    let wheel = 0;
    let pinch: number | undefined;
    const distance = (e: TouchEvent) => {
      const [a, b] = [e.touches[0], e.touches[1]];
      return a && b ? Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY) : 0;
    };
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey || !zoom.current) return;
      e.preventDefault();
      wheel += e.deltaY;
      if (Math.abs(wheel) < ZOOM_WHEEL_STEP) return;
      zoom.current(wheel < 0 ? 1 : -1);
      wheel = 0;
    };
    const onTouchStart = (e: TouchEvent) => {
      pinch = e.touches.length === 2 ? distance(e) : undefined;
    };
    const onTouchMove = (e: TouchEvent) => {
      if (pinch === undefined || e.touches.length !== 2 || !zoom.current) return;
      e.preventDefault();
      const now = distance(e);
      if (now > pinch * ZOOM_PINCH_STEP || now < pinch / ZOOM_PINCH_STEP) {
        zoom.current(now > pinch ? 1 : -1);
        pinch = now;
      }
    };
    const onTouchEnd = () => {
      pinch = undefined;
    };
    element.addEventListener('wheel', onWheel, { passive: false });
    element.addEventListener('touchstart', onTouchStart, { passive: true });
    element.addEventListener('touchmove', onTouchMove, { passive: false });
    element.addEventListener('touchend', onTouchEnd);
    return () => {
      element.removeEventListener('wheel', onWheel);
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchmove', onTouchMove);
      element.removeEventListener('touchend', onTouchEnd);
    };
  }, []);

  useEffect(() => () => clearTimeout(keyboard.current?.timer), []);

  /** Day column and vertical position (0..1) under a viewport point; nearest column outside the grid. */
  const locate = (x: number, y: number): { index: number; fraction: number } => {
    const rects = columnRefs.current.map((c) => c?.getBoundingClientRect());
    let index = rects.findIndex((r) => r && x >= r.left && x < r.right);
    if (index < 0) index = x < (rects[0]?.left ?? 0) ? 0 : days.length - 1;
    const rect = rects[index];
    const fraction = rect && rect.height > 0 ? (y - rect.top) / rect.height : 0;
    // Drag near the top/bottom edge of the viewport: scroll along.
    const view = scroller.current?.getBoundingClientRect();
    if (view && scroller.current) {
      if (y < view.top + 24) scroller.current.scrollTop -= 12;
      else if (y > view.bottom - 24) scroller.current.scrollTop += 12;
    }
    return { index, fraction };
  };
  const dayAt = (index: number) => days[Math.max(0, Math.min(days.length - 1, index))] ?? days[0] ?? now;

  const startCreate = (index: number) => (e: PointerEvent<HTMLDivElement>) => {
    if (!onSelectRange || e.target !== e.currentTarget) return;
    // No text selection from dragging over free time (it would take the right-click to the browser menu).
    if (e.pointerType === 'mouse') e.preventDefault();
    const from = locate(e.clientX, e.clientY).fraction;
    const day = dayAt(index);
    let last = from;
    begin(e, {
      move: (x, y) => {
        last = locate(x, y).fraction;
        setPreview(createRange(day, from, last, false));
      },
      end: (click) => {
        setPreview(undefined);
        const range = createRange(day, from, last, click);
        onSelectRange(range.start, range.end, false);
      },
      cancel: () => setPreview(undefined),
    });
  };

  const startAllDay = (index: number) => (e: PointerEvent<HTMLDivElement>) => {
    if (!onSelectRange || e.target !== e.currentTarget) return;
    // No text selection from dragging over free time (it would take the right-click to the browser menu).
    if (e.pointerType === 'mouse') e.preventDefault();
    let to = index;
    begin(e, {
      move: (x) => {
        const rects = allDayRefs.current.map((c) => c?.getBoundingClientRect());
        const hit = rects.findIndex((r) => r && x >= r.left && x < r.right);
        if (hit >= 0) to = hit;
        setAllDayPreview({ from: index, to });
      },
      end: () => {
        setAllDayPreview(undefined);
        const range = allDayRange(dayAt(index), dayAt(to));
        onSelectRange(range.start, range.end, true);
      },
      cancel: () => setAllDayPreview(undefined),
    });
  };

  const grab = (event: CalendarGridEvent) => (e: PointerEvent<HTMLElement>, edge: 'start' | 'end' | undefined) => {
    if (!draggable(event) || !onChangeEvent) return;
    const original = { start: event.start, end: event.end };
    // Ctrl (⌘) or Alt held when the drag starts: a copy goes to the new place (Outlook).
    const copy = !edge && (e.ctrlKey || e.metaKey || e.altKey);
    const at = locate(e.clientX, e.clientY);
    // Keep the grab point inside the event while moving (minutes between pointer and event start).
    const pointerMinutes = minutesAt(at.fraction, 1);
    const startMinutes = event.start.getHours() * 60 + event.start.getMinutes();
    const offset = pointerMinutes - startMinutes;
    let next = original;
    begin(e, {
      move: (x, y) => {
        const { index, fraction } = locate(x, y);
        const day = dayAt(index);
        if (edge) {
          next = resizeEdge(original, edge, edge === 'end' && original.end.getTime() - startOfDay(original.start).getTime() >= DAY_MS ? day : startOfDay(original.start), minutesAt(fraction));
        } else {
          const minutes = Math.round((minutesAt(fraction, 1) - offset) / SNAP_MINUTES) * SNAP_MINUTES;
          next = moveTo(original, day, Math.max(0, Math.min(24 * 60 - SNAP_MINUTES, minutes)));
        }
        setPreview({ id: event.id, ...next });
      },
      end: (click) => {
        setPreview(undefined);
        if (click || sameRange(next, original)) return;
        if (copy && onCopyEvent) onCopyEvent(event.id, next.start, next.end);
        else onChangeEvent(event.id, next.start, next.end);
      },
      cancel: () => setPreview(undefined),
    });
  };

  const keyMove = (event: CalendarGridEvent) => (e: KeyboardEvent<HTMLButtonElement>) => {
    if (!e.altKey || !draggable(event) || !onChangeEvent) return;
    const steps: Record<string, [number, number]> = { ArrowUp: [-SNAP_MINUTES, 0], ArrowDown: [SNAP_MINUTES, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] };
    const step = steps[e.key];
    if (!step) return;
    if (e.shiftKey && step[1] !== 0) return;
    e.preventDefault();
    const base = preview?.id === event.id ? preview : { start: event.start, end: event.end };
    const next = nudge(base, e.shiftKey ? 'resize' : 'move', step[0], step[1]);
    setPreview({ id: event.id, ...next });
    clearTimeout(keyboard.current?.timer);
    keyboard.current = {
      id: event.id,
      timer: setTimeout(() => {
        keyboard.current = undefined;
        setPreview(undefined);
        if (!sameRange(next, event)) onChangeEvent(event.id, next.start, next.end);
      }, KEYBOARD_COMMIT_MS),
    };
  };

  const select = (id: string) => {
    if (!suppressClick.current) onSelectEvent(id);
  };
  const ordered = inTimeOrder(shown.filter((e) => days.some((d) => overlapsDay(e, d))));
  const tabStop = tabStopOf(ordered, selectedId);
  const createPreview = preview && !preview.id ? preview : undefined;
  const moving = preview?.id;
  const allDayRangeShown = allDayPreview ? allDayRange(dayAt(allDayPreview.from), dayAt(allDayPreview.to)) : selection?.allDay ? selection : undefined;
  const allDayFirst = allDayPreview ? Math.min(allDayPreview.from, allDayPreview.to) : days.findIndex((day) => selection?.allDay && day >= startOfDay(selection.start) && day < selection.end);
  /** Pixel height of an event: one line below ~2 lines, the time line from ~2.5 lines. */
  const pixels = (fraction: number) => fraction * 24 * hourPx;

  return (
    <section aria-label={labels.grid} data-no-native-menu="" onKeyDown={rovingKeyDown(ordered)} className={cn('flex min-h-0 flex-1 flex-col select-none', className)}>
      {/* Keyboard moves (Alt+arrows) are announced while they wait to be saved. */}
      <div aria-live="polite" className="sr-only">
        {preview?.id ? eventLabel({ ...(events.find((e) => e.id === preview.id) ?? { id: '', title: '', allDay: false, color: 'blue' }), start: preview.start, end: preview.end }) : ''}
      </div>
      <div className="grid shrink-0 border-b" style={columns}>
        {secondaryZone ? (
          <>
            <div className="flex items-end justify-end px-1 pb-1 text-xs text-muted-foreground">{secondaryZone.label}</div>
            <div className="flex items-end justify-end px-1 pb-1 text-xs text-muted-foreground">{primaryZoneLabel}</div>
          </>
        ) : (
          <div className="flex items-end justify-end px-1 pb-1 text-xs text-muted-foreground">{weekLabel}</div>
        )}
        {days.map((day) => {
          const today = sameDay(day, now);
          return (
            <button
              key={day.getTime()}
              type="button"
              onClick={() => onSelectDay?.(day)}
              disabled={!onSelectDay}
              aria-current={today ? 'date' : undefined}
              className="flex flex-col items-center gap-0.5 border-l py-1.5 text-xs outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-default"
            >
              <span className="text-muted-foreground">{formatWeekday(day)}</span>
              <span className={cn('flex size-7 items-center justify-center rounded-full text-sm font-semibold', today && 'bg-primary text-primary-foreground')}>
                {formatDayNumber(day)}
              </span>
            </button>
          );
        })}
      </div>
      <div className="grid shrink-0 border-b" style={columns}>
        <div className="px-1 py-1 text-right text-xs text-muted-foreground" style={{ gridColumn: `span ${gutters}` }}>
          {labels.allDay}
        </div>
        <div className="relative min-w-0" style={{ gridColumn: `span ${days.length}`, minHeight: `${Math.max(1, allDayLanes) * 1.375 + 0.5}rem` }}>
          {/* Day cells: click/drag to select days, right-click for the slot menu. */}
          <div className="absolute inset-0 grid" style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))` }}>
            {days.map((day, index) => {
              const inPreview = allDayPreview
                ? index >= Math.min(allDayPreview.from, allDayPreview.to) && index <= Math.max(allDayPreview.from, allDayPreview.to)
                : Boolean(selection?.allDay && day >= startOfDay(selection.start) && day < selection.end);
              return (
                <div
                  key={day.getTime()}
                  ref={(el) => {
                    allDayRefs.current[index] = el;
                  }}
                  data-allday={day.toISOString()}
                  data-context-id={SLOT_CONTEXT_ID}
                  onPointerDown={startAllDay(index)}
                  onDoubleClick={(e) => e.target === e.currentTarget && onOpenSelection?.()}
                  onContextMenu={(e) => {
                    if (e.target !== e.currentTarget || !onSelectRange || inPreview) return;
                    const range = allDayRange(day, day);
                    onSelectRange(range.start, range.end, true);
                  }}
                  className={cn('min-w-0 border-l', inPreview && 'bg-primary/15 ring-1 ring-primary ring-inset')}
                />
              );
            })}
          </div>
          {/* Bars across their days (multi-day events continue into the next/previous range). */}
          <div
            className="pointer-events-none relative grid gap-y-0.5 p-0.5"
            style={{ gridTemplateColumns: `repeat(${days.length}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${Math.max(1, allDayLanes)}, 1.25rem)` }}
          >
            {allDayLayout.shown.map((segment) => (
              <div key={segment.event.id} className="pointer-events-auto flex min-w-0 px-px" style={{ gridColumn: `${segment.from + 1} / ${segment.to + 2}`, gridRow: segment.lane + 1 }}>
                <EventChip
                  event={segment.event}
                  compact
                  className={cn('h-5 w-full shrink-0', segment.continuesBefore && 'rounded-l-none border-l-0', segment.continuesAfter && 'rounded-r-none')}
                  selected={selectedId === segment.event.id}
                  onSelect={select}
                  label={eventLabel(segment.event)}
                  contextId={segment.event.id}
                  tabIndex={tabStop === segment.event.id ? 0 : -1}
                />
              </div>
            ))}
            {allDayFirst >= 0 && allDayRangeShown && formatSelection && (
              <span aria-hidden className="truncate px-1 text-xs font-medium whitespace-nowrap" style={{ gridColumn: `${allDayFirst + 1} / -1`, gridRow: allDayLanes + 1 }}>
                {formatSelection({ ...allDayRangeShown, allDay: true })}
              </span>
            )}
          </div>
        </div>
      </div>
      <div ref={scroller} className="min-h-0 flex-1 overflow-y-auto">
        <div className="relative grid" style={{ ...columns, height: gridHeight }}>
          {secondaryZone && (
            <div className="relative border-r border-border/60">
              {HOURS.map((hour) => (
                <span key={hour} className="absolute right-1 -translate-y-1/2 text-xs text-muted-foreground" style={{ top: `${(hour / 24) * 100}%` }}>
                  {hour > 0 ? secondaryZone.formatHour(hour) : ''}
                </span>
              ))}
            </div>
          )}
          <div className="relative">
            {HOURS.map((hour) => (
              <span key={hour} className="absolute right-1 -translate-y-1/2 text-xs text-muted-foreground" style={{ top: `${(hour / 24) * 100}%` }}>
                {hour > 0 ? formatHour(hour) : ''}
              </span>
            ))}
          </div>
          {days.map((day, index) => {
            const today = sameDay(day, now);
            const dayStart = startOfDay(day).getTime();
            const nowFraction = (now.getTime() - dayStart) / (addDays(startOfDay(day), 1).getTime() - dayStart);
            const range = createPreview ?? (selection && !selection.allDay ? selection : undefined);
            const ghost = range && range.start.getTime() < dayStart + DAY_MS && range.end.getTime() > dayStart ? range : undefined;
            const workday = workingHours?.days.includes(day.getDay());
            return (
              <div
                key={day.getTime()}
                ref={(el) => {
                  columnRefs.current[index] = el;
                }}
                data-day={day.toISOString()}
                data-context-id={SLOT_CONTEXT_ID}
                className={cn('relative touch-pan-y border-l', today && 'bg-primary/5')}
                onPointerDown={startCreate(index)}
                onDoubleClick={(e) => e.target === e.currentTarget && onOpenSelection?.()}
                onContextMenu={(e) => {
                  // Right-click outside the selection first selects the slot under the pointer (Outlook).
                  if (e.target !== e.currentTarget || !onSelectRange) return;
                  const slot = createRange(day, locate(e.clientX, e.clientY).fraction, 0, true);
                  if (!selection || selection.allDay || slot.start < selection.start || slot.start >= selection.end) onSelectRange(slot.start, slot.end, false);
                }}
              >
                {workingHours &&
                  (workday ? (
                    <>
                      <div aria-hidden data-nonworking="" className="pointer-events-none absolute inset-x-0 top-0 bg-muted/60" style={{ height: `${(workingHours.startMinutes / 1440) * 100}%` }} />
                      <div aria-hidden data-nonworking="" className="pointer-events-none absolute inset-x-0 bottom-0 bg-muted/60" style={{ height: `${((1440 - workingHours.endMinutes) / 1440) * 100}%` }} />
                    </>
                  ) : (
                    <div aria-hidden data-nonworking="" className="pointer-events-none absolute inset-0 bg-muted/60" />
                  ))}
                {HOURS.map((hour) => (
                  <div key={hour} aria-hidden className="pointer-events-none absolute inset-x-0 border-t border-border/60" style={{ top: `${(hour / 24) * 100}%` }} />
                ))}
                {timed[index]?.map(({ event, top, height, column, columns: count }) => (
                  <div
                    key={event.id}
                    className={cn('absolute z-10 flex p-px', moving === event.id && 'z-30')}
                    style={{ top: `${top * 100}%`, height: `${height * 100}%`, left: moving === event.id ? '0%' : `${(column / count) * 100}%`, width: moving === event.id ? '100%' : `${100 / count}%` }}
                  >
                    <EventChip
                      event={event}
                      // Short events and narrow overlap columns: title only (time and place in the tooltip).
                      className={cn(count >= 3 ? 'flex-1 items-start pt-0.5' : 'flex-1', moving === event.id && 'shadow-lg ring-2 ring-ring')}
                      compact={pixels(height) < 30 || count >= 3}
                      selected={selectedId === event.id}
                      onSelect={select}
                      label={eventLabel(event)}
                      time={(pixels(height) >= 40 && count < 3) || moving === event.id ? formatTimeRange(event) : undefined}
                      draggable={draggable(event)}
                      resizable={draggable(event)}
                      onGrab={grab(event)}
                      onKeyDown={keyMove(event)}
                      contextId={event.id}
                      tabIndex={tabStop === event.id ? 0 : -1}
                    />
                  </div>
                ))}
                {ghost && (
                  <div
                    aria-hidden
                    data-selection-label=""
                    className="pointer-events-none absolute inset-x-0.5 z-0 overflow-hidden rounded-sm border-2 border-primary bg-primary/15 p-0.5 text-xs font-medium text-foreground"
                    style={{
                      top: `${(Math.max(0, ghost.start.getTime() - dayStart) / DAY_MS) * 100}%`,
                      height: `${((Math.min(dayStart + DAY_MS, ghost.end.getTime()) - Math.max(dayStart, ghost.start.getTime())) / DAY_MS) * 100}%`,
                    }}
                  >
                    {/* Opaque label, readable over events below the selection. */}
                    <span className="inline-block max-w-full rounded-sm bg-background px-1 leading-tight shadow-sm">
                      {formatSelection ? formatSelection({ start: ghost.start, end: ghost.end, allDay: false }) : formatTimeRange({ id: '', title: '', start: ghost.start, end: ghost.end, allDay: false, color: 'blue' })}
                    </span>
                  </div>
                )}
                {today && (
                  <div aria-hidden className="pointer-events-none absolute inset-x-0 z-20 border-t-2 border-destructive" style={{ top: `${nowFraction * 100}%` }}>
                    <span className="absolute -top-1.5 -left-1.5 size-3 rounded-full bg-destructive" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
